//Software Details Component

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { IconButton } from "@mui/material";
import { parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import useStarRating from "../hooks/useStarRating";


const getTooltipContent = (stars) => {
  switch (stars) {
    case 5:
      return "Fully Documented AI Planning Software";
    case 4:
      return "Comprehensive Coverage of AI Planning Software";
    case 3:
      return "Moderate Coverage of AI Planning Software";
    case 2:
      return "Partial Coverage of AI Planning Software";
    case 1:
      return "Insufficient Details of AI Planning Software";
    default:
      return "No Rating Available";
  }
};

function renderDocumentation(docString) {
  if (!docString) return "N/A";
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = docString.split(urlRegex);
  return parts.map((part, idx) =>
    urlRegex.test(part) ? (
      <a
        key={idx}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-500 underline"
      >
        {part}
      </a>
    ) : (
      <span key={idx}>{part}</span>
    )
  );
}

const ToolDetails = ({ tools }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [tool, setTool] = useState(undefined);

  // Spinner Component
  const Spinner = () => (
    <div
      className="flex justify-center items-center h-full"
      style={{ minHeight: "80vh" }}
    >
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-green-800"></div>
    </div>
  );

  useEffect(() => {
    // Scroll to the top
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {

    const fetchData = () => {
      const foundTool = tools.find((tool) => tool.id === id);
      setTimeout(() => {
        setTool(foundTool);
        setLoading(false); // Hide spinner
      }, 200);
    };

    fetchData();
  }, [id, tools]);

  // Ensure minimum height for the container
  return (
    <div className="px-0 py-6" style={{ minHeight: "80vh" }}>
      {loading ? (
        <Spinner />
      ) : tool ? (
        <>
          <div className="flex items-center mb-4">
            <IconButton
              onClick={() => navigate(-1)}
              style={{ marginRight: "16px" }}
              sx={{
                backgroundColor: "#e0e0e0",
                "&:hover": {
                  backgroundColor: "#bdbdbd",
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <h2 className="text-2xl font-bold">{tool.shortName || "AI Planner"}</h2>
          </div>

          {/* Tool Name, Contributors, Year */}
          <div className="mb-4 p-4 border rounded bg-gray-50">
            <div className="text-xl font-bold mb-2">
              {tool.longName || "N/A"}
            </div>
            <div className="flex flex-wrap gap-4 text-gray-700">
              <span><b>Contributors:</b> {tool.contributors || "N/A"}</span>
            </div>
            <div className="text-gray-700 mt-1"><b>Year:</b> {tool.year || "N/A"}</div>
          </div>

          {/* Planner Overview */}
          <div className="mb-4 p-4 border rounded bg-gray-50">
            <div className="flex items-center mb-2">
              <div className="w-2 h-6 bg-blue-600 mr-2 rounded"></div>
              <div className="text-lg font-bold">Planner Overview</div>
            </div>
            <div>{tool.explanationAbstract || "N/A"}</div>
          </div>

          {/* References & Planner Details as Table */}
          <div className="mb-4 p-4 border rounded bg-gray-50">
            <div className="flex items-center mb-2">
              <div className="w-2 h-6 bg-blue-600 mr-2 rounded"></div>
              <div className="text-lg font-bold">References & Planner Details</div>
            </div>
            <table className="min-w-full border-collapse">
              <tbody>
                <tr>
                  <td className="p-2 font-semibold" style={{width: '290px'}}>References:</td>
                  <td className="p-2">
                    {tool.plannerReferences ? (
                      <div>
                        {tool.plannerReferences
                          ?.split(";")
                          .map((reference, index) => {
                            const trimmedReference = reference.trim();
                            if (!trimmedReference) return null;
                            const isLink = trimmedReference.startsWith("http");
                            return (
                              <div key={index}>
                                [{index + 1}]
                                {isLink ? (
                                  <a
                                    href={trimmedReference}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-500 underline"
                                  >
                                    {" "}{trimmedReference}
                                  </a>
                                ) : (
                                  " " + trimmedReference
                                )}
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      "N/A"
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="p-2 font-semibold" style={{width: '290px'}}>Planning Language:</td>
                  <td className="p-2">{tool.planningLanguage || "N/A"}</td>
                </tr>
                <tr>
                  <td className="p-2 font-semibold" style={{width: '290px'}}>Planning Classes:</td>
                  <td className="p-2">{tool.planningClasses ? tool.planningClasses?.split(",").join(", ") : "N/A"}</td>
                </tr>
                <tr>
                  <td className="p-2 font-semibold" style={{width: '290px'}}>Planner Functionality Type:</td>
                  <td className="p-2">{tool.planningType ? tool.planningType?.split(",").join(", ") : "N/A"}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Resources & Technical Details as Table */}
          <div className="mb-4 p-4 border rounded bg-gray-50">
            <div className="flex items-center mb-2">
              <div className="w-2 h-6 bg-blue-600 mr-2 rounded"></div>
              <div className="text-lg font-bold">Resources & Technical Details</div>
            </div>
            <table className="min-w-full border-collapse">
              <tbody>
                <tr>
                  <td className="p-2 font-semibold" style={{width: '290px'}}>Documentation:</td>
                  <td className="p-2">{renderDocumentation(tool.documentation)}</td>
                </tr>
                <tr>
                  <td className="p-2 font-semibold" style={{width: '290px'}}>Executable:</td>
                  <td className="p-2">{renderDocumentation(tool.executable)}</td>
                </tr>
                <tr>
                  <td className="p-2 font-semibold" style={{width: '290px'}}>Source Code:</td>
                  <td className="p-2">
                    {tool.sourceCode ? (
                      <div>
                        {tool.sourceCode
                          ?.split(";")
                          .map((reference, index) => {
                            const trimmedReference = reference.trim();
                            if (!trimmedReference) return null;
                            const isLink = trimmedReference.startsWith("http");
                            return (
                              <div key={index}>
                                [{index + 1}]
                                {isLink ? (
                                  <a
                                    href={trimmedReference}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-500 underline"
                                  >
                                    {" "}{trimmedReference}
                                  </a>
                                ) : (
                                  " " + trimmedReference
                                )}
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      "N/A"
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="p-2 font-semibold" style={{width: '290px'}}>Implementation Language(s):</td>
                  <td className="p-2">{tool.implementationLanguage || "N/A"}</td>
                </tr>
                <tr>
                  <td className="p-2 font-semibold" style={{width: '290px'}}>Supported Operating System(s):</td>
                  <td className="p-2">{tool.operatingSystem?.split(",").join(", ") || "N/A"}</td>
                </tr>
                <tr>
                  <td className="p-2 font-semibold" style={{width: '290px'}}>Source Code Dependencies:</td>
                  <td className="p-2">{renderDocumentation(tool.environmentNotes)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      ) : (
        !loading &&
        tool === null && (
          <div className="text-red-500" style={{ minHeight: "80vh" }}>
            Tool not found!
          </div>
        )
      )}
    </div>
  );
};



const StarRating = ({ completenessScore }) => {
  const { stars, color } = useStarRating(completenessScore);

  return (
    <div className={`flex ${color} text-xl`}>
      {Array.from({ length: 5 }, (_, index) => (
        <FontAwesomeIcon
          key={index}
          icon={faStar}
          className={`inline-block ${index < stars ? color : "text-gray-300"}`}
          title={getTooltipContent(stars)}
        />
      ))}
    </div>
  );
};


export default ToolDetails;
